import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef, type SortingState } from '@tanstack/react-table';
import { Badge, LoadingSpinner, Table, TextLink, Tooltip } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import { DocumentCoverageCell, type DocumentCoverageColumn } from '@/features/document-templates';
import { useDealersByIds, buildDealerNameMap } from '@/features/dealers/hooks/useDealersByIds';
import { EyeIcon, EditIcon } from 'lucide-react';
import type { Product } from '@/features/products/types/products.types';

interface Props {
    data: Product[];
    isLoading?: boolean;
    sorting?: SortingState;
    onSortingChange?: (s: SortingState) => void;
    documentCoverage?: DocumentCoverageColumn;
}

const documentsColumn = ({ byProductId, status }: DocumentCoverageColumn): ColumnDef<Product> => ({
    id: 'documents',
    header: 'Documents',
    enableSorting: false,
    cell: (info) => {
        const id = info.row.original.id;
        const coverage = byProductId.get(id);
        return (
            <DocumentCoverageCell
                status={status === 'ready' && !coverage ? 'error' : status}
                missing={coverage?.missing ?? []}
                documentsPath={`/smr-admin/products/${id}?tab=documents`}
            />
        );
    },
});

export const ProductDataTable: React.FC<Props> = ({ data, isLoading = false, sorting, onSortingChange, documentCoverage }) => {
    const navigate = useNavigate();
    const dealerIds = data.map((p) => p.parent_dealer_id).filter((id): id is string => Boolean(id));
    const { data: dealers, isLoading: isDealersLoading } = useDealersByIds(dealerIds);
    const dealerNameMap = buildDealerNameMap(dealers);

    const columns = useMemo<ColumnDef<Product>[]>(() => [
        {
            accessorKey: 'name',
            header: 'Name',
            cell: (info) => <span className="font-semibold text-sm">{info.getValue() as string}</span>,
        },
        {
            accessorKey: 'description',
            header: 'Description',
            cell: (info) => <span className="text-sm">{(info.getValue() as string) || '—'}</span>,
        },
        {
            accessorKey: 'status_key',
            header: 'Status',
            cell: (info) => {
                const key = info.getValue() as string | undefined;
                return (
                    <Badge size="xs" variant={key === 'active' ? 'success' : 'error'}>
                        {key ?? '—'}
                    </Badge>
                );
            },
        },
        {
            id: 'parent_dealer',
            header: 'Parent Dealer',
            enableSorting: false,
            cell: (info) => {
                const id = info.row.original.parent_dealer_id;
                if (!id) return <span className="text-sm text-mp-muted">—</span>;
                if (isDealersLoading) return <span className="text-sm text-mp-muted font-heading">Loading...</span>;
                const name = dealerNameMap.get(id);
                return (
                    <TextLink to={`/admin/dealers/${id}`} color="secondary">
                        {name ?? '—'}
                    </TextLink>
                );
            },
        },
        {
            accessorKey: 'effective_from',
            header: 'Effective From',
            cell: (info) => {
                const v = info.getValue() as string | null | undefined;
                return <span className="text-sm">{v ? new Date(v).toLocaleDateString() : '—'}</span>;
            },
        },
        {
            accessorKey: 'deleted_at',
            header: 'Retired At',
            cell: (info) => {
                const v = info.getValue() as string | null | undefined;
                return <span className="text-sm">{v ? new Date(v).toLocaleDateString() : '—'}</span>;
            },
        },
        {
            accessorKey: 'created_at',
            header: 'Created',
            cell: (info) => <span className="text-sm">{new Date(info.getValue() as string).toLocaleDateString()}</span>,
        },
        ...(documentCoverage ? [documentsColumn(documentCoverage)] : []),
        {
            id: 'actions',
            header: 'Actions',
            enableSorting: false,
            cell: (info) => (
                <div className="flex space-x-2">
                    <Tooltip content="View product">
                        <TextLink
                            ariaLabel="View product"
                            onClick={() => navigate(`/smr-admin/products/${info.row.original.id}`)}
                            color="primary"
                            startIcon={<EyeIcon size={20} />}
                        />
                    </Tooltip>
                    {!info.row.original.deleted_at && (
                        <PermissionGuard permission={{ resource: 'smr', action: 'product.edit' }}>
                            <Tooltip content="Edit product">
                                <TextLink
                                    ariaLabel="Edit product"
                                    onClick={() => navigate(`/smr-admin/products/${info.row.original.id}/edit`)}
                                    color="secondary"
                                    startIcon={<EditIcon size={18} />}
                                />
                            </Tooltip>
                        </PermissionGuard>
                    )}
                </div>
            ),
        },
    ], [isDealersLoading, dealerNameMap, navigate, documentCoverage]);

    if (isLoading) {
        return <LoadingSpinner size="xl" className="py-12" message="Loading products..." />;
    }

    return (
        <Table<Product>
            data={data}
            columns={columns}
            sorting={sorting}
            onSortingChange={onSortingChange}
        />
    );
};
